import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  FormGroup,
  Input,
  InsetText,
  Label,
  Loader,
  LoaderButton,
  Row,
  Select,
  Text,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { BackButton, ConfirmModal } from 'slovlex-idsk-react-components';
import routes from '../../../routing/routes';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import './VyjadreniaPredbeznaInformacia.scss';
import mapDate from '../../../function/mapDate';
import { useClient } from '../../../context/ClientStatusContext';
import { startVyjadrenia } from '../../../client/pracovnyKrok/vyjadreniaClient';
import {
  datePlusDays,
  getDurations, getInitialConfigDuration,
  includeSviatkyInEndDate,
  isSviatok,
  isToday,
} from '../../../function/pripomienkovanieFunctions';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import useSviatky from '../../../hooks/useSviatky';
import LegislativnyMaterialDetail
  from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { changeVyjadrenia } from '../../../client/admin/vyjadreniaConfigClient';

interface Props {
  materialDetail: LegislativnyMaterialDetail;
  config?: boolean;
  admin?: boolean;
}

const VyjadreniaPredbeznaInformacia = ({
  materialDetail,
  config = false,
  admin = false,
}: Props): ReactElement => {
  const now = new Date();
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    sviatky,
    sviatkyStatus,
  } = useSviatky();
  const [startDate, setStartDate] = useState<Date>(
    config ? materialDetail.datumZaciatkuPkMpk : now,
  );
  const [vyjadreniaDurationDays, setVyjadreniaDurationDays] = useState<number>();
  const [endDate, setEndDate] = useState<Date>(
    config ? materialDetail.datumUkonceniaPkMpk : datePlusDays(startDate, vyjadreniaDurationDays),
  );
  const [durationsDays, setDurationsDays] = useState<Map<number, string>>(new Map());
  const {
    type,
    year,
    index,
    pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();

  useEffect(updateEndDate, [vyjadreniaDurationDays, startDate,
    sviatky, config, materialDetail.datumZaciatkuPkMpk, materialDetail.datumUkonceniaPkMpk]);
  useEffect(calculateDurations, [config, admin, materialDetail.sablonaProcesu]);
  useEffect(() => {
    if ((config || admin) && sviatky && !vyjadreniaDurationDays) {
      setVyjadreniaDurationDays(
        getInitialConfigDuration(
          materialDetail.datumZaciatkuPkMpk,
          materialDetail.datumUkonceniaPkMpk,
          sviatky,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sviatky]);

  function updateEndDate(): void {
    if (vyjadreniaDurationDays > 9999) {
      setVyjadreniaDurationDays(9999);
    }
    const daysToAdd = vyjadreniaDurationDays - 1;
    let newEndDate = datePlusDays(startDate, daysToAdd);

    newEndDate = includeSviatkyInEndDate(startDate, newEndDate, sviatky);

    setEndDate(newEndDate);
  }

  function calculateDurations(): void {
    setDurationsDays(getDurations(materialDetail.sablonaProcesu));
    if (!config && !admin) {
      setVyjadreniaDurationDays(materialDetail.sablonaProcesu.lehotaPrednastavenaHodnota);
    }
  }

  const {
    wrapClient: vyjadreniaClient,
    status: vyjadreniaRequestStatus,
  } = useClient(config ? changeVyjadrenia : startVyjadrenia);
  const vyjadreniaPredbeznejInformacie = async (): Promise<void> => {
    if (config) {
      await vyjadreniaClient(type, year, index, vyjadreniaDurationDays, startDate);
    } else {
      await vyjadreniaClient(type, year, index, pracovnyKrokId, vyjadreniaDurationDays, startDate);
    }
    navigate(routes.procesy.detail.withParams(type, year, index));
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const getSubmitButtonLabel = (): string => {
    if (config) {
      return 'Zmeniť konfiguráciu doby pre Vyjadrovanie';
    }
    if (isToday(startDate)) {
      return 'Prejsť do štádia Vyjadrenia k predbežnej informácii';
    }
    return 'Nakonfigurovať spustenie štádia Vyjadrenia k predbežnej informácii';
  };

  const getQuestion = (): string => {
    if (config) {
      return 'Naozaj si prajete uložiť novú konfiguráciu vyjadrení k predbežnej informácii?';
    }
    if (isToday(startDate)) {
      return 'Naozaj si prajete spustiť štádium "Vyjadrenia k predbežnej informácii"?';
    }
    return 'Naozaj si prajete nastaviť spustenie štádia "Vyjadrenia k predbežnej informácii" na neskorší dátum?';
  };

  const getDescription = (): string => {
    if (config) {
      return '';
    }
    if (isToday(startDate)) {
      return 'Po vykonaní tohto pracovného kroku sa ukončí aktuálne štádium'
        + ' a spustí sa štádium Vyjadrenia k predbežnej informácií.';
    }
    return 'Keďže je dátum začiatku nastavený na neskorší dátum, po vykonaní tohto '
      + 'pracovného kroku zostane materiál v aktuálnom štádiu až do nastaveného dátumu, pričom '
      + 'bude možné vykonať iba jeden pracovný krok - "Zrušiť nastavené štádium Vyjadrenia k predbežnej '
      + 'informácii". Keď nastane určený dátum začiatku, materiál prejde do štádia Vyjadrenia k predbežnej '
      + 'informácii.';
  };

  let dobaTrvaniaVyjadrovaniaInput;
  if (config || admin) {
    dobaTrvaniaVyjadrovaniaInput = (
      <Input
        type="text"
        autoComplete="off"
        id="durationDays"
        width="full"
        maxLength={4}
        value={vyjadreniaDurationDays || ''}
        error={getFieldErrorMessage('durationDays')}
        onChange={e => {
          clearFieldErrorMessage('durationDays');
          const inputValue = e.target.value;
          const numericValue = parseInt(inputValue, 10);
          if (!Number.isNaN(numericValue)) {
            setVyjadreniaDurationDays(numericValue);
          }
          if (inputValue === '') {
            setVyjadreniaDurationDays(inputValue);
          }
        }}
      />
    );
  } else {
    dobaTrvaniaVyjadrovaniaInput = (
      <Select
        id="vyjadreniaDurationDays"
        width="full"
        value={vyjadreniaDurationDays}
        onChange={e => setVyjadreniaDurationDays(e.target.value)}
      >
        {(Array.from(durationsDays) && Array.from(durationsDays)?.length > 0) ? (
          <>
            {Array.from(durationsDays)
              .map(([numberOfDays, readableText]) => (
                <option key={numberOfDays} value={numberOfDays}>{readableText}</option>
              ))}
          </>
        ) : (
          <option key="emptyOption" value="emptyOption" disabled>
            Nenašiel sa žiadny záznam.
          </option>
        )}
      </Select>
    );
  }

  return (
    <div className="vyjadrenia-predbezna-informacia">
      <Row>
        <Col widthDesktop="one-third">
          <FormGroup error={!!getFieldErrorMessage('startDate')}>
            <Label htmlFor="zmenaOd">Dátum začiatku vyjadrovania</Label>
            <DatePicker
              id="startDate"
              width="full"
              showIcon
              minDate={config ? undefined : now}
              maxDate={config ? now : undefined}
              value={startDate}
              onChange={newDate => {
                clearFieldErrorMessage('startDate');
                setStartDate(newDate);
              }}
              error={getFieldErrorMessage('startDate')}
            />
          </FormGroup>
        </Col>
      </Row>
      {isSviatok(startDate, sviatky) && (
        <InsetText id="startDateWarning" type="warn" className="datum-zaciatku-warning__inset-text">
          <Text type="span" bold>Dátum začiatku štádia Vyjadrenia k predbežnej informácii ste
            nakonfigurovali na deň pracovného pokoja. Tento dátum bude viditeľný v detaile
            legislatívneho procesu. Lehota sa začne počítať od nasledujúceho pracovného dňa.
          </Text>
        </InsetText>
      )}
      <Row>
        <Col widthDesktop="one-third">
          <FormGroup error={!!getFieldErrorMessage('vyjadreniaDurationDays')}>
            <Label htmlFor="vyjadreniaDurationDays">Doba trvania vyjadrovania (pracovných dní)</Label>
            {dobaTrvaniaVyjadrovaniaInput}
          </FormGroup>
          <Loader loading={sviatkyStatus.loading}>
            <FormGroup>
              <Label htmlFor="endDate">Dátum ukončenia vyjadrovania (vrátane)</Label>
              <span className="idsk-hint">Dátum ukončenia vyjadrovania bude vyrátaný automaticky
                po zadaní doby trvania vyjadrovania.
              </span>
              <Input id="endDate" type="text" width="full" disabled value={mapDate(endDate)} />
            </FormGroup>
          </Loader>
        </Col>
      </Row>
      <div className="button-container-wrapper">
        <ConfirmModal
          question={getQuestion()}
          description={getDescription()}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              className="vyjadrenia-predbezna-informacia__submit-button"
              size="large"
              id="odoslatButton"
              loading={vyjadreniaRequestStatus.loading}
              disableOutsideClickWhileLoading
            >{getSubmitButtonLabel()}
            </LoaderButton>
          )}
          doAfterConfirm={() => vyjadreniaPredbeznejInformacie()}
        />
        <BackButton disabled={vyjadreniaRequestStatus?.loading} />
      </div>
    </div>
  );
};

export default VyjadreniaPredbeznaInformacia;
